import { useContext } from 'react';
import { Close, LocalOffer } from '@mui/icons-material';
import {
  Divider,
  Typography,
  Grid2,
  Box,
  Drawer,
  Toolbar,
} from '@mui/material';
import {
  createSearchParams,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router';
import qs from 'qs';
import { useMediaQuery } from '@mui/system';
import Alert from 'components/alerts';
import { GlobalTypesContext, ProjectPermissionContext } from 'context';
import { formatDate } from 'utils';
import { StyledIconButton } from 'components/buttons';
import config from 'config';
import formatParameterInfo from '../helpers';
import DataDrawerGrid from '../common/grid';
import History from '../common/version-history';
import Status from '../common/status-history';
import Tags from '../common/tags';
import { StyledGrid, StyledCard } from '../common/details-pane-styles';
import Value from './value';
import Details from './details';
import { useParameter, useParameterDetails } from './hooks';
import DataCardHeader from './header';

function DataDrawer() {
  const { parameterTagsAndTypes, allDisciplines } =
    useContext(GlobalTypesContext);
  const { writePermission } = useContext(ProjectPermissionContext);
  const { search } = useLocation();
  const searchString = { ...qs.parse(search, { ignoreQueryPrefix: true }) };
  const { error, setError, parameter, setParameter, loading, commentLoading } =
    useParameter();
  const { parameterValue, valueWithUnit } = formatParameterInfo(parameter);
  const { parameterId, projectId, assetId } = useParams();
  const navigate = useNavigate();
  const screenXs = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const screenMd = useMediaQuery(theme => theme.breakpoints.down('lg'));

  const {
    parameterTags,
    setParameterTags,
    parameterDisciplines,
    setParameterDisciplines,
    isTagLoading,
    setSelectedHistoryRowId,
    statusHistory,
    statusLoading,
  } = useParameterDetails(parameterId, parameter);

  const resetToParamListView = () => {
    navigate({
      pathname: assetId
        ? `/projects/${projectId}/asset/${assetId}/parameters`
        : `/projects/${projectId}/parameters`,

      search: `?${createSearchParams({ ...searchString })}`,
    });
  };

  const drawerContents = (
    <Box
      sx={{
        overflowX: 'auto',
        maxHeight: 'calc(100vh - 64px)',
      }}
    >
      <Grid2 container>
        <Grid2 item size={screenMd ? 10 : 11}>
          <DataCardHeader
            loading={loading}
            parameter={parameter}
            setError={setError}
            parameterError={error}
            error={error}
          />
        </Grid2>
        <Grid2 item size={screenMd ? 2 : 1}>
          <StyledIconButton
            key="close"
            tooltipProps={{ placement: 'left-end' }}
            title="Close Parameter Details"
            sx={{ float: 'left' }}
            onClick={resetToParamListView}
            aria-label="close-editing-button"
            icon={<Close />}
            data-cy="close-source-icon"
          />
        </Grid2>
      </Grid2>
      <Divider />
      {error && (
        <Box
          sx={theme => ({
            padding: theme.spacing(3),
          })}
        >
          <Alert
            title={error?.msg}
            text={error?.details}
            onClose={
              parameter.id
                ? () => {
                    setError(null);
                  }
                : () => {}
            }
          />
        </Box>
      )}
      <StyledCard>
        <StyledGrid
          aria-label="content"
          container
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Value
            parameterValue={parameterValue}
            valueWithUnit={valueWithUnit}
          />
          <br />
          <Details
            loading={loading}
            parameter={parameter}
            setParameter={setParameter}
            setError={setError}
            commentLoading={commentLoading}
            parameterValue={parameterValue}
            valueWithUnit={valueWithUnit}
          />
          <History
            parameter={parameter}
            header={
              parameter?.selectedEntry
                ? `Updated on ${String(
                    formatDate(parameter?.selectedEntry?.createdAt, undefined)
                  )} by ${parameter?.selectedEntry?.createdBy?.email}`
                : 'Not defined'
            }
            setSelectedHistoryRowId={setSelectedHistoryRowId}
          />
          {/* TO DO - refactor status history once multiple entries is implemented */}
          {config.featureFlags.isParameterQAEnabled && (
            <Status
              parameter={parameter}
              header={
                parameter?.selectedEntry?.status
                  ? `${
                      statusHistory[0]?.status ||
                      parameter?.selectedEntry?.status ||
                      'unanswered'
                    } on ${formatDate(statusHistory[0]?.date, undefined)} by ${
                      statusHistory[0]?.updatedBy?.email
                    }`
                  : 'Not defined'
              }
              statusHistory={statusHistory}
              statusLoading={statusLoading}
            />
          )}
          <DataDrawerGrid
            icon={<LocalOffer />}
            caption=""
            content={
              <Grid2 container aria-label="tags component">
                <Typography variant="caption" color="textSecondary">
                  Tags
                </Typography>
                {parameterTagsAndTypes.map(tagType => (
                  <Tags
                    id={parameterId}
                    existingTags={parameterTags}
                    setExistingTags={setParameterTags}
                    tagType={tagType}
                    globalTags={tagType?.tags}
                    key={tagType?.id}
                    loading={isTagLoading}
                    referenceTable="parameter"
                    permission={writePermission}
                  />
                ))}
                <Tags
                  title="disciplines"
                  id={parameterId}
                  existingTags={parameterDisciplines}
                  setExistingTags={setParameterDisciplines}
                  tagType={{
                    name: 'Disciplines',
                  }}
                  globalTags={allDisciplines}
                  key="disciplines"
                  loading={isTagLoading}
                  referenceTable="parameter"
                  permission={writePermission}
                />
              </Grid2>
            }
          />
        </StyledGrid>
      </StyledCard>
    </Box>
  );

  return (
    <Drawer
      aria-label="parameter-detail-panel"
      slotProps={{
        paper: {
          sx: {
            width: screenXs ? '293px' : '40vw',
          },
        },
      }}
      open
      anchor="right"
      onClose={resetToParamListView}
    >
      <Toolbar />
      {drawerContents}
    </Drawer>
  );
}
export default DataDrawer;
